import clsx from 'clsx';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

// Libs
import { mainMenu, bottomMenu } from '@/lib/platform/menu/menu';

// Hooks
import useToggle from '@/utils/hooks/utils/useToggle';

// Components
import Icon from '@/components/base/Icon';
import Iconosquare from '@/components/icons/Iconosquare';
import { Wrapper } from '@/components/layouts/LayoutPlatform/components/Menu/MenuMobile/Sidebar/Wrapper';
import { MenuMobileEntries } from '@/components/layouts/LayoutPlatform/components/Menu/MenuMobile/Sidebar/components/MenuMobileEntries';
import { MenuMobileSubEntries } from '@/components/layouts/LayoutPlatform/components/Menu/MenuMobile/Sidebar/components/MenuMobileSubEntries';

// Types
import type { MenuEntryType } from '@/lib/platform/menu/menu';

export type SideBarProps = {
    isMenuOpen: boolean;
    toggleMenu: () => void;
};

export function SideBar({ isMenuOpen, toggleMenu }: SideBarProps) {
    return (
        <Wrapper isMenuOpen={isMenuOpen} toggleMenu={toggleMenu}>
            <div className="mx-5 mb-3 flex items-center justify-between">
                <Link href="/" className="inline-block leading-none" data-testid="link-home-page">
                    <Iconosquare size={24} className="text-icoBlue" />
                </Link>
                <button
                    data-testid="button-close-menu-mobile"
                    onClick={toggleMenu}
                    className="flex items-center text-icoGray-500 dark:text-icoDarkMode-wolf">
                    <Icon icon="close" />
                </button>
            </div>
            <SideBarContent />
        </Wrapper>
    );
}

function SideBarContent() {
    const wrapperScrollRef = useRef<HTMLDivElement>(null);
    const { isOpen, toggle } = useToggle();
    const [activeEntry, setActiveEntry] = useState<MenuEntryType['id']>(null);

    useEffect(() => {
        if (!isOpen && activeEntry) {
            setTimeout(() => setActiveEntry(null), 300);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleActiveEntry = (id: MenuEntryType['id']) => {
        setActiveEntry(id);
        wrapperScrollRef.current.scrollTo({ top: 0 });
        toggle();
    };

    return (
        <div
            data-testid="menu-mobile-sidebar-content"
            ref={wrapperScrollRef}
            className="scrollbar relative flex h-full overflow-x-hidden p-5 align-top">
            <div
                className={clsx(
                    'flex w-full min-w-full flex-col justify-between gap-4 transition-all duration-300',
                    {
                        '-translate-x-56 overflow-hidden': isOpen,
                        'translate-x-0': !isOpen
                    }
                )}>
                <MenuMobileEntries
                    menuConfig={mainMenu}
                    activeEntry={activeEntry}
                    setActiveEntry={handleActiveEntry}
                />
                <MenuMobileEntries
                    menuConfig={bottomMenu}
                    activeEntry={activeEntry}
                    setActiveEntry={handleActiveEntry}
                />
            </div>
            <div
                className={clsx('flex w-full min-w-full transition-all duration-300', {
                    'translate-x-6 overflow-hidden': !isOpen,
                    '-translate-x-full': isOpen
                })}>
                <MenuMobileSubEntries activeEntry={activeEntry} toggleSubEntries={toggle} />
            </div>
        </div>
    );
}
